import React, { useEffect, useMemo, useState } from "react";
import StaffSidebar from "../components/StaffSidebar";
import axios from "axios";
import Pagination from "../components/Pagination";
import "../assets/css/staff-dashboard.css";

export default function CoursesPage() {
  const programme = localStorage.getItem("programme") || "";
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isHOD = role === "hod";

  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_BASE_URL || "http://localhost:3003";
  const PAGE_SIZE = 12;

  const [courses, setCourses] = useState([]);
  const [levels, setLevels] = useState(["All"]);
  const [courseSearch, setCourseSearch] = useState("");
  const [courseLevel, setCourseLevel] = useState("All");
  const [coursePage, setCoursePage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingCourseId, setSavingCourseId] = useState(null);

  const [editingCourseId, setEditingCourseId] = useState(null);
  const [editForm, setEditForm] = useState({
    CourseTitle: "",
    CreditUnits: "",
  });

  const headers = useMemo(() => {
    return token ? { Authorization: `Bearer ${token}` } : undefined;
  }, [token]);

  const loadLevels = async () => {
    if (!programme) return;

    try {
      const res = await axios.get(`${API}/api/staff/courses`, {
        params: { programme },
        headers,
      });

      const rows = Array.isArray(res.data) ? res.data : [];
      const mapped = rows
        .map((item) =>
          typeof item === "string"
            ? item
            : item?.Level || item?.level || item?.CourseLevel || "",
        )
        .map((lvl) => String(lvl).trim())
        .filter(Boolean);

      setLevels(["All", ...new Set(mapped)]);
    } catch (err) {
      console.error("Failed to fetch course levels:", err);
      setLevels(["All"]);
    }
  };

  const loadCourses = async () => {
    if (!programme) return;

    try {
      setLoading(true);

      const res = await axios.get(`${API}/api/staff/courses`, {
        params: {
          programme,
          level: courseLevel === "All" ? "" : courseLevel,
          search: courseSearch.trim(),
        },
        headers,
      });

      const rows = Array.isArray(res.data) ? res.data : [];
      setCourses(rows);
      setCoursePage(1);
      setEditingCourseId(null);
      setEditForm({ CourseTitle: "", CreditUnits: "" });
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, programme, headers]);

  useEffect(() => {
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, programme, courseLevel, courseSearch, headers]);

  const totalPages = Math.ceil(courses.length / PAGE_SIZE);
  const pagedCourses = courses.slice(
    (coursePage - 1) * PAGE_SIZE,
    coursePage * PAGE_SIZE,
  );

  const startEdit = (course) => {
    if (!isHOD) return;

    setEditingCourseId(course.CourseID);
    setEditForm({
      CourseTitle: course.CourseTitle ?? "",
      CreditUnits:
        course.CreditUnits === null || course.CreditUnits === undefined
          ? ""
          : String(course.CreditUnits),
    });
  };

  const cancelEdit = () => {
    setEditingCourseId(null);
    setEditForm({ CourseTitle: "", CreditUnits: "" });
  };

  const handleSave = async (courseId) => {
    if (!isHOD) return;

    try {
      setSavingCourseId(courseId);

      await axios.put(
        `${API}/api/staff/courses/${courseId}`,
        {
          programme,
          CourseTitle: editForm.CourseTitle,
          CreditUnits:
            editForm.CreditUnits === "" ? null : Number(editForm.CreditUnits),
        },
        { headers },
      );

      await loadCourses();
    } catch (err) {
      console.error("Failed to update course:", err);
      alert(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          "Failed to update course.",
      );
    } finally {
      setSavingCourseId(null);
    }
  };

  return (
    <div className="staff-dashboard" style={{ marginTop: "-130px" }}>
      <StaffSidebar />

      <main>
        <h1>Courses</h1>

        <section className="panel">
          <div className="controls">
            <input
              type="search"
              placeholder="Search courses…"
              value={courseSearch}
              onChange={(e) => setCourseSearch(e.target.value)}
            />

            <select
              value={courseLevel}
              onChange={(e) => setCourseLevel(e.target.value)}
            >
              {levels.map((lvl) => (
                <option key={lvl} value={lvl}>
                  {lvl}
                </option>
              ))}
            </select>
          </div>

          {!isHOD && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "10px 12px",
                borderRadius: 8,
                background: "#fff7e6",
                color: "#8a6d3b",
                fontWeight: 600,
              }}
            >
              You can view courses, but only HODs can edit course titles and
              credit units.
            </div>
          )}

          <table className="table modern">
            <thead>
              <tr>
                <th>Code</th>
                <th>Title</th>
                <th>Level</th>
                <th>Units</th>
                <th style={{ width: 170 }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {pagedCourses.map((c) => {
                const isEditing = editingCourseId === c.CourseID;

                return (
                  <tr key={c.CourseID}>
                    <td>{c.CourseCode}</td>

                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          className="course-edit-input"
                          value={editForm.CourseTitle}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              CourseTitle: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        c.CourseTitle
                      )}
                    </td>

                    <td>{c.Level}</td>

                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          className="course-edit-input course-edit-input-number"
                          value={editForm.CreditUnits}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              CreditUnits: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        (c.CreditUnits ?? "-")
                      )}
                    </td>

                    <td>
                      {isHOD ? (
                        isEditing ? (
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              type="button"
                              onClick={() => handleSave(c.CourseID)}
                              disabled={savingCourseId === c.CourseID}
                              className="course-action-btn course-save-btn"
                            >
                              {savingCourseId === c.CourseID
                                ? "Saving..."
                                : "Save"}
                            </button>

                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={savingCourseId === c.CourseID}
                              className="course-action-btn course-cancel-btn"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEdit(c)}
                            className="course-action-btn course-edit-btn"
                          >
                            Edit
                          </button>
                        )
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })}

              {pagedCourses.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    {loading ? "Loading courses..." : "No courses found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <Pagination
            page={coursePage}
            total={totalPages}
            onChange={setCoursePage}
          />
        </section>
      </main>
    </div>
  );
}
